/**
 * useFirestore
 * 現在のappIdを使用したFirestoreアクセスを提供するカスタムフック
 */
import { useMemo } from 'react';
import { useAppId } from '@/contexts/AppIdContext';
import { CollectionReference, DocumentReference, collection, doc } from 'firebase/firestore';
import {
  getCurrentExamCollection as getCollection,
  getCurrentExamDoc as getDoc,
  getUsersCollection,
  getUserDoc,
  getCurrentExamMetadata as getMetadata
} from '@/services/firestoreService';
import { db } from '@/firebase';

/**
 * 現在のappIdを使用したFirestoreヘルパー関数を提供
 */
export function useFirestore() {
  const { appId } = useAppId();

  return useMemo(() => ({
    /**
     * 現在のアプリのコレクションを取得
     */
    getCurrentExamCollection: (collectionName: string): CollectionReference => {
      return getCollection(collectionName, appId);
    },

    /**
     * 現在のアプリのドキュメントを取得
     */
    getCurrentExamDoc: (collectionName: string, docId: string): DocumentReference => {
      return getDoc(collectionName, docId, appId);
    },

    /**
     * usersコレクションを取得（全アプリ共通）
     */
    getUsersCollection,

    /**
     * 特定のユーザードキュメントを取得（全アプリ共通）
     */
    getUserDoc,

    /**
     * 現在のアプリのメタデータドキュメントを取得
     */
    getCurrentExamMetadata: (): DocumentReference => {
      return getMetadata(appId);
    },

    /**
     * 現在のアプリIDを取得
     */
    getCurrentExamId: (): string => {
      return appId;
    },

    /**
     * アプリ固有のユーザードキュメントを取得
     * Path: apps/{appId}/users/{userId}
     */
    getAppUserDoc: (userId: string): DocumentReference => {
      return doc(db, 'apps', appId, 'users', userId);
    },

    /**
     * アプリ固有のユーザーサブコレクションを取得
     * Path: apps/{appId}/users/{userId}/{collectionName}
     */
    getAppUserCollection: (userId: string, collectionName: string): CollectionReference => {
      return collection(db, 'apps', appId, 'users', userId, collectionName);
    }
  }), [appId]);
}
